import { FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { TLotesSelecionados, TProdutosDetalhes } from "../../../../../../../../../types/api/comercial/detalhar-pedido";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { permitirApenasNumeros } from "../../../../../../../../../helpers/masks";

export default function QuantidadeLotes({
    data
}: QuantidadeLotesProps) {

    const { setValue, watch } = useFormContext()

    const lotesSelecionados = watch("lotes_selecionados") as TLotesSelecionados;

    const handleCheckLoteValue = useCallback((): IListaLotes[] => {
        return data.lotes_disponiveis.map((lote) => {
            if (!lotesSelecionados) {
                const quantidadeDisponivel = Math.min(lote.estoque_atual, data.quantidade - data.quantidade_entregue);
                return {
                    lote: lote.lote,
                    vencimento: lote.vencimento,
                    estoque_atual: lote.estoque_atual,
                    quantidade: quantidadeDisponivel.toString(),
                }
            }

            const selectedLote = lotesSelecionados.find((selected) => selected.lote === lote.lote);
            return {
                lote: lote.lote,
                vencimento: lote.vencimento,
                estoque_atual: lote.estoque_atual,
                quantidade: selectedLote ? selectedLote.quantidade.toString() : "0",
            };
        })
    }, [data.lotes_disponiveis, data.quantidade, data.quantidade_entregue, lotesSelecionados]);

    const handleUpdateQuantidade = (lote: string, quantidade: string) => {

        if (!lotesSelecionados || (lotesSelecionados && lotesSelecionados.length === 0)) {
            setValue("lotes_selecionados", [{ lote, quantidade: Number(quantidade) }])
            return
        }
        if (lotesSelecionados && lotesSelecionados.length > 0) {
            const loteIndex = lotesSelecionados.findIndex((selected) => selected.lote === lote);
            if (loteIndex > -1) {
                const updatedLotes = [...lotesSelecionados];
                updatedLotes[loteIndex].quantidade = Number(quantidade);
                setValue("lotes_selecionados", updatedLotes)
            } else {
                setValue("lotes_selecionados", [...lotesSelecionados, { lote, quantidade: Number(quantidade) }])
            }
        }

        setListaLotes((prevState) => {
            const updatedLotes = prevState.map((item) => {
                if (item.lote === lote) {
                    return { ...item, quantidade: quantidade };
                }
                return item;
            });
            return updatedLotes;
        });
    };

    const [mostrarTodos, setMostrarTodos] = useState<boolean>(false)
    const [listaLotes, setListaLotes] = useState<IListaLotes[]>(handleCheckLoteValue())

    const listagem: IListaLotes[] = useMemo(() => {
        if (mostrarTodos) return listaLotes
        return listaLotes.filter((lote) => (
            (lote.estoque_atual > 0 || Number(lote.quantidade) > 0 || lote.quantidade === "")
        ))
    }, [mostrarTodos, listaLotes])

    useEffect(() => {
        if (lotesSelecionados && lotesSelecionados.length > 0) {
            const quantidadeNosLotes = lotesSelecionados.reduce((acc, current) => acc + current.quantidade, 0);
            setValue("quantidade_para_expedir", quantidadeNosLotes);
        } else {
            let quantidadeRestante = data.quantidade - data.quantidade_entregue;

            const lotesAtualizados = listaLotes.map((lote) => {
            if (quantidadeRestante <= 0) {
                return { ...lote, quantidade: "0" };
            }

            const quantidadeParaAdicionar = Math.min(lote.estoque_atual, quantidadeRestante);
            quantidadeRestante -= quantidadeParaAdicionar;

            return { ...lote, quantidade: quantidadeParaAdicionar.toString() };
            });

            setListaLotes(lotesAtualizados);
            setValue(
            "lotes_selecionados",
            lotesAtualizados
                .filter((lote) => Number(lote.quantidade) > 0)
                .map((lote) => ({
                lote: lote.lote,
                quantidade: Number(lote.quantidade),
                }))
            );
        }

    }, [lotesSelecionados, data])

    return (
        <>
            <FormControlLabel
                control={<Switch value={mostrarTodos} onChange={() => setMostrarTodos(prevState => !prevState)} />}
                label="Mostrar lotes sem disponibilidade"
                componentsProps={{
                    typography: {
                        variant: "subtitle2"
                    }
                }}
            />

            <TableContainer sx={{ height: 300, overflow: "auto", pr: 1 }} >
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Lotes</b></TableCell>
                            <TableCell align="center"><b>Vencimento</b></TableCell>
                            <TableCell align="right"><b>Disponível</b></TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {listagem.map((row) => {
                            const quantidadeSelecionada = lotesSelecionados?.find((selected) => selected.lote === row.lote)?.quantidade ?? row.quantidade;
                            return (
                                <TableRow
                                    key={row.lote}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell>
                                        {row.lote}
                                    </TableCell>

                                    <TableCell align="center">
                                        {row.vencimento}
                                    </TableCell>

                                    <TableCell align="right">
                                        {row.estoque_atual}
                                    </TableCell>

                                    <TableCell align="right">
                                        <TextField
                                            fullWidth
                                            value={quantidadeSelecionada}
                                            label="Qtd"
                                            variant="outlined"
                                            type="text"
                                            onChange={(e) => handleUpdateQuantidade(row.lote, permitirApenasNumeros(e.target.value))}
                                        />
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

interface IListaLotes {
    lote: string,
    vencimento: string,
    estoque_atual: number,
    quantidade: string,
}

interface QuantidadeLotesProps {
    data: TProdutosDetalhes
}
